import role2Schema from '../../../../assets/forms/role-2.form.json';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildWarningBag,
  sheetHasTriggeredWarning,
  WarnableControl,
} from './warning-eval.helper';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  isRole2ControlDisabled,
  isRole2UnconditionallyRequired,
  ROLE2_REQUIRED_AXIS,
  Role2RequiredDetectable,
} from './role2-scope-of-filing.rules';

/**
 * Pure validator for Role 2 (Scope of Filing).
 *
 * Mirrors the editor's role2-020000-scope-of-filing.component.ts validation
 * surface — specifically what drives `scopeForm.valid` inside
 * setupStatePersistence (the value the mount-based path publishes via
 * `updateSheetLevelStatus({ hasErrors: !isValid })`).
 *
 * What mount-based `scopeForm.valid` actually checks:
 *   - Non-axis controls: `Validators.required` attached when the schema marks
 *     the control as unconditionally required (either `required: true` OR
 *     an unconditional `required` entry in `validators[]`). Role 2 has no
 *     conditionalOn entries in validators[], no min/pattern validators, and
 *     its `setupConditionalValidation()` targets `ssmt-scope:*` controls
 *     that are NOT present in role-2.form.json — so those valueChanges
 *     subscriptions are no-ops and cannot flip form.valid.
 *   - Axis controls (fields with `ContextPolicy.allowedAxes`): buildForm
 *     attaches `Validators.required` ONLY when the axis equals
 *     `ssmt:BusinessOneMember` AND `control.required === true`. CLBG-axis
 *     controls (ObjectOne/Two/Three members) are NEVER flagged required on
 *     the form even when their schema entries have `required: true`, because
 *     the axis check in buildForm uses the business axis name literally.
 *     Payload filtering would be a separate concern from `form.valid`, and
 *     none exists today (the never-wired `FormSanitizationService` was
 *     deleted 2026-10-21).
 *
 * CLBG branching (FS-CLBG / KFI-CLBG):
 *   Intentionally NOT gated here. The mount-based path does not gate form
 *   validity on submission type (the CLBG axis required attribute never
 *   attaches regardless), so adding CLBG-conditional required-checks here
 *   would break parity. If mount-based behaviour changes to require CLBG
 *   fields for CLBG submissions, this validator must be updated in lockstep.
 *
 * Disabled controls (DescriptionOfBusiness):
 *   `ssmt:DescriptionOfBusiness` carries `ui.readonly`, so buildForm()
 *   builds it `{ disabled: true }` and Angular excludes it from
 *   `form.valid`. `isRole2ControlDisabled` (see rules file) subtracts it
 *   from the required set so this validator agrees. The mandatory error
 *   belongs to the MSIC Code driver, which is enabled and required — the
 *   description is derived from it by `populateDescriptionsFromMsicCodes()`
 *   and the user can never type into it, so an error there would be
 *   unclearable. Same rule as `ssmt-dei:TypeOfSubmission` deferring to its
 *   Basis driver on role0/role1.
 *
 *   This supersedes a required-key alias (description satisfied by its
 *   sibling MSIC key) that existed to stop a false X on the first paint of
 *   a converted submission, where the backend ships the code and the editor
 *   derives the description. Excluding the key outright covers that case too.
 *
 * Tier rationale:
 *   Registered as Tier 2 per the Phase 2 plan classification (same-sheet
 *   requiredIf / hideWhen surface). The current mount-based validity check
 *   does not flip on requiredIf/hideWhen because the editor keeps controls
 *   attached with their static validators regardless of visibility; this
 *   validator mirrors that. If the editor later tightens requiredIf logic,
 *   the Tier 2 slot leaves room to add same-sheet conditional checks here
 *   without churning the registry.
 */

interface Role2SchemaControl extends Role2RequiredDetectable {
  qname: string;
  ContextPolicy?: { allowedAxes?: ReadonlyArray<string> };
}

const SCHEMA_CONTROLS: ReadonlyArray<Role2SchemaControl> =
  role2Schema.controls as Role2SchemaControl[];

/**
 * Precomputed set of effective required keys. Non-axis controls contribute
 * their qname directly; axis controls contribute `${qname}[${axis}]` only
 * for the `ssmt:BusinessOneMember` axis (matching buildForm's narrow gate).
 *
 * Computed once at module load because it is pure schema-driven — no sheet
 * data or context changes the set.
 */
const STATIC_REQUIRED_KEYS: ReadonlySet<string> = (() => {
  const keys = new Set<string>();
  for (const control of SCHEMA_CONTROLS) {
    if (!isRole2UnconditionallyRequired(control)) continue;

    // A disabled control fires no validations — buildForm() builds it with
    // `{ disabled: true }` and Angular drops it from `form.valid`. See
    // isRole2ControlDisabled for why this is the rule and not an exception.
    if (isRole2ControlDisabled(control)) continue;

    const axes = control.ContextPolicy?.allowedAxes ?? [];
    if (axes.length === 0) {
      keys.add(control.qname);
      continue;
    }

    // Axis controls: mirror buildForm's `axis === ROLE2_REQUIRED_AXIS` gate.
    // CLBG axes (ssmt-clbg:ObjectOneMember, etc.) deliberately fall through
    // without being added — their required flag never reaches the form.
    if (axes.includes(ROLE2_REQUIRED_AXIS)) {
      keys.add(`${control.qname}[${ROLE2_REQUIRED_AXIS}]`);
    }
  }
  return keys;
})();

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function checkRequiredErrors(sheetData: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const key of STATIC_REQUIRED_KEYS) {
    if (isEmpty(sheetData[key])) {
      errors[key] = `${key} is required`;
    }
  }
  return errors;
}

export const role2ScopeOfFilingValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.scopeOfFiling,
  tier: 2,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const errors = checkRequiredErrors(sheetData);
    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: sheetHasTriggeredWarning(
        role2Schema.controls as unknown as WarnableControl[],
        sheetData,
        buildWarningBag(
          context.crossSheetData?.get(SHEET_KEYS.filingInfo) ??
            context.crossSheetData?.get(SHEET_KEYS.filingPrerequisites) ??
            {},
          sheetData,
          // Scope's warnings compare against itself (circulation-vs-approval)
          // and auditorsReport (AGM-vs-auditor); nest the full cross-sheet map
          // + own key so they resolve, matching the editor's hand-wired bag.
          { sheets: context.crossSheetData, sheetKey: SHEET_KEYS.scopeOfFiling }
        )
      ),
      errors,
    };
  },
};
